/**
 * #648 — "did this click land on the card's focus region?"
 *
 * The pointer twin of `Lt2HotkeyListener`'s `ownedByInteractive`, and
 * deliberately NOT the same function. The keyboard fence asks "does this
 * surface consume letter keys?", and it WHITELISTS buttons inside
 * `[data-lt2-hotkey-control]` so the LT2 line/perspective/price controls hand
 * the next A/D press back to the focused card. This one asks "is this element
 * an action, or is it the card body?", and that whitelist would be exactly
 * wrong here: `data-lt2-hotkey-control` is painted on the focused ROW, so
 * reusing it would un-fence every price pill in the focused card — the bug
 * #648 exists to close. Keep the two predicates separate.
 *
 * The other divergence runs the same way: `role="button"` is fenced here and
 * absent from the keyboard list. `CancelLevelControl` and `RestingOrderChip`
 * are `<span role="button">`, so the keyboard fence cannot see them at all.
 *
 * Before #648, focus was set in the CAPTURE phase, which React runs top-down
 * before any descendant's bubble handler — so a nested action's
 * `stopPropagation()` could suppress the row's expansion but never protect the
 * focus. Focus now moves on the bubble, and this predicate decides whether the
 * click belonged to the card body or to something inside it.
 *
 * Every unknown resolves to "not the focus region": a click we cannot classify
 * must not move the hotkey target.
 */

/** Explicit opt-in. Beats the action rules below. */
export const LT2_FOCUS_REGION_ATTR = 'data-lt2-focus-region';

/** Explicit opt-out, for non-semantic clickables. Beats the opt-in. */
export const LT2_NO_FOCUS_ATTR = 'data-lt2-no-focus';

/**
 * Anything a POINTER acts on. Semantic elements first, then the ARIA roles
 * that make a non-semantic node behave like one.
 */
const POINTER_ACTION_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'textarea',
  'select',
  'dialog',
  '[popover]',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="textbox"]',
  '[role="searchbox"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="menu"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="dialog"]',
].join(',');

/** The nearest matching ancestor, but only when it is inside `container`. */
function closestWithin(el: Element, selector: string, container: Element): Element | null {
  const hit = el.closest(selector);
  // A marker on an ANCESTOR of the container belongs to some outer surface,
  // not to this card — it must not answer for a click inside this one.
  return hit != null && container.contains(hit) ? hit : null;
}

/**
 * True when `target` is the card's own body rather than an action inside it.
 *
 * `container` is the focusable card's root — pass `e.currentTarget`, so the
 * question is always scoped to the card that owns the handler.
 *
 * A `[data-lt2-focus-region]` marker is a PROMISE that the region contains no
 * order, navigation, or menu action. Never place one around a control that
 * submits, cancels, edits, or navigates.
 */
export function isLt2FocusRegionClick(target: EventTarget | null, container: Element): boolean {
  if (!(target instanceof Element)) return false;
  if (!container.contains(target)) return false;
  // A shadow root hides its interior from closest(): the event has already
  // retargeted to the host, so an action inside would read as card body. We
  // cannot see in, so we refuse. A custom-element tag (the dash is the spec's
  // own marker) is treated the same, where `shadowRoot` can read null.
  if (target.shadowRoot != null || target.tagName.includes('-')) return false;

  if (closestWithin(target, `[${LT2_NO_FOCUS_ATTR}]`, container) != null) return false;
  if (closestWithin(target, `[${LT2_FOCUS_REGION_ATTR}]`, container) != null) return true;
  return closestWithin(target, POINTER_ACTION_SELECTOR, container) == null;
}
